import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import {Avatar} from 'hylo/avatar';
import {Button} from 'hylo/button';
import {Group} from 'hylo/layout';
import {Menu} from 'hylo/menu';
import {memo, useCallback} from 'react';
import type {MouseEvent, ReactNode} from 'react';
import {Link as RouterLink, useInRouterContext} from 'react-router';

import {clearDatadogRumUser} from '#lib/datadog.js';
import {resetMixpanelUser} from '#lib/mixpanel.js';
import {locale} from '#locale/index.js';
import {globalAppsLocale} from '#locale/translations/global_apps.js';
import {userMenuLocale} from '#locale/translations/user_menu.js';
import {useShouldBeClientRouted} from '#ui/atoms/link/hooks.js';

import * as styles from './style.css.js';

// Keep React/legacy route selection while Hylo owns the menu's presentation.
const RoutedMenuItem = memo(function RoutedMenuItem({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  const clientRouted = useShouldBeClientRouted(href);
  return (
    <Menu.Item render={<RouterLink to={href} reloadDocument={!clientRouted} />}>
      {children}
    </Menu.Item>
  );
});

export interface UserMenuProps {
  name: string;
  profileURL?: string;
  organizationURL?: string;
  adminURL?: string;
  globalAppsURL?: string;
  debugURL?: string;
}

// Each shell supplies only the links allowed by its existing Pundit checks.
export const UserMenu = memo(function UserMenu({
  name,
  profileURL,
  organizationURL,
  adminURL,
  globalAppsURL,
  debugURL,
}: UserMenuProps) {
  const text = userMenuLocale[locale.current];
  const inRouter = useInRouterContext();
  const handleLogout = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    resetMixpanelUser();
    void clearDatadogRumUser().finally(() => {
      window.location.assign('/auth/logout');
    });
  }, []);
  const links = [
    {href: profileURL, label: text.profile, icon: PersonIcon},
    {href: organizationURL, label: text.organization, icon: CorporateFareIcon},
    {href: adminURL, label: text.admin, icon: SettingsIcon},
    {
      href: globalAppsURL,
      label: globalAppsLocale[locale.current].title,
      icon: LanguageIcon,
    },
    {href: debugURL, label: text.debug, icon: TroubleshootIcon},
  ];

  return (
    <Menu.Root>
      <Menu.Trigger render={<Button aria-label={name} kind="text" />}>
        <Avatar size={32} initial={name.slice(0, 1).toUpperCase()} />
        <span>{name}</span>
        <KeyboardArrowDownIcon fontSize="small" />
      </Menu.Trigger>
      <Menu.Content
        align="end"
        sideOffset={8}
        aria-label={text.menu}
        className={styles.popup}
      >
        <Menu.List>
          {links.map(({href, label, icon: ItemIcon}) =>
            href ? (
              inRouter ? (
                <RoutedMenuItem key={href} href={href}>
                  <Group gap="sm">
                    <ItemIcon fontSize="small" />
                    {label}
                  </Group>
                </RoutedMenuItem>
              ) : (
                <Menu.Item
                  key={href}
                  render={<a href={href} aria-label={label} />}
                >
                  <Group gap="sm">
                    <ItemIcon fontSize="small" />
                    {label}
                  </Group>
                </Menu.Item>
              )
            ) : null
          )}
          <Menu.Item
            render={<a href="/auth/logout" aria-label={text.logout} />}
            onClick={handleLogout}
          >
            <Group gap="sm">
              <LogoutIcon fontSize="small" />
              {text.logout}
            </Group>
          </Menu.Item>
        </Menu.List>
      </Menu.Content>
    </Menu.Root>
  );
});
